import { React, useState } from 'react'

import {
  Box,
  Flex,
  IconButton,
  useColorMode,
  Link,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  Button,
  HStack,
  VStack,
  useBreakpointValue,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuGroup,
  Text,
  useToast,
  Avatar,
  Icon,
  MenuDivider,
  Spacer,
} from '@chakra-ui/react'
import {
  UserIcon,
  Cog6ToothIcon,
  ChatBubbleLeftRightIcon,
  BookOpenIcon,
  ArrowLeftEndOnRectangleIcon,
} from '@heroicons/react/24/outline'
import { motion } from 'framer-motion'
import { FaMoon, FaSun } from 'react-icons/fa'
import { FaBars } from 'react-icons/fa'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { MenuToggleDropdownIcon } from '../../assets/SvgIcons'
import { logout } from '../../redux/features/authSlice'
import { calculateMS } from '../../utils'

const feedbackFormURL = 'https://forms.office.com/r/8aUUu0pxgt'
const manualURL = 'https://sway.office.com/Zs1QUzGTaeJMlELf?ref=Link'

const Navbar = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const toast = useToast()

  const { colorMode, toggleColorMode } = useColorMode()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const bgColor = useColorModeValue('gray.100', 'gray.900')
  const logoColor = useColorModeValue('#23417D', 'white')

  const user = useSelector(state => state.auth.user)

  function toggleDrawer() {
    setIsDrawerOpen(!isDrawerOpen)
  }

  function closeDrawer() {
    setIsDrawerOpen(false)
  }

  function onLogout() {
    dispatch(
      logout({
        callback: () => {
          navigate('/')
          toast({
            title: 'Logged out',
            status: 'success',
            description: 'See you again next time!',
            duration: calculateMS(3),
            isClosable: true,
            position: 'top',
          })
        },
      })
    )
  }

  const MotionBox = motion(Box)

  const itemVariants = {
    hidden: { opacity: 0, x: '-100%' },
    show: { opacity: 1, x: '0' },
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const current_url = location.pathname

  const navItemBoxStyles = {
    _hover: { borderBottom: '2px solid white' },
    _active: { transform: 'translateY(1px)' },
  }

  const navItems = [
    {
      url: '/',
      name: 'Home',
    },
    {
      url: '/directory',
      name: 'Directory',
    },
    {
      url: '/casework',
      name: 'Casework',
    },
    {
      url: '/tools',
      name: 'Tools',
    },
  ]

  const dropdownItems = [
    {
      onClick: () => navigate(`/directory/staff/${user?.id}`),
      icon: UserIcon,
      name: 'Profile',
    },
    {
      onClick: () => navigate('/admin'),
      icon: Cog6ToothIcon,
      visibility: user?.is_admin,
      name: 'Admin Settings',
    },
    {
      onClick: () => window.open(feedbackFormURL, '_blank'),
      icon: ChatBubbleLeftRightIcon,
      name: 'Feedback Form',
    },
    {
      onClick: () => window.open(manualURL, '_blank'),
      icon: BookOpenIcon,
      name: 'Manual',
    },
  ]

  const NavigationItems = () => {
    return (
      <HStack spacing={9}>
        {navItems.map((item, index) => (
          <Box
            key={index}
            {...navItemBoxStyles}
            borderBottom={
              current_url === item.url
                ? '2px solid white'
                : '2px solid transparent'
            }
          >
            <Link href={item.url} _hover={{ textDecoration: 'none' }}>
              {item.name}
            </Link>
          </Box>
        ))}
      </HStack>
    )
  }

  const DropdownItems = () => {
    return (
      <Box>
        <Menu
          placement='bottom-end'
          bg={colorMode === 'dark' ? 'blue.100' : 'nyccBlue.800'}
        >
          <MenuButton as={Button} variant='unstyled'>
            <MenuToggleDropdownIcon />
          </MenuButton>
          <MenuList
            maxWidth={250}
            color={colorMode === 'dark' ? 'white' : 'gray.600'}
          >
            <MenuGroup>
              {dropdownItems.map(
                (item, index) =>
                  (item.visibility === undefined ||
                    item.visibility === true) && (
                    <MenuItem
                      key={index}
                      textTransform='uppercase'
                      onClick={item.onClick}
                    >
                      <Icon as={item.icon} mr={4} />
                      {item.name}
                    </MenuItem>
                  )
              )}
              <MenuDivider />
              <MenuItem
                textTransform='uppercase'
                onClick={toggleColorMode}
                aria-label='Toggle color mode'
                variant='ghost'
              >
                <Icon as={colorMode === 'light' ? FaMoon : FaSun} mr={4} />
                {colorMode === 'light' ? 'Dark Mode' : 'Light Mode'}
              </MenuItem>
              <MenuDivider />
              <MenuItem textTransform='uppercase' onClick={() => onLogout()}>
                <Icon as={ArrowLeftEndOnRectangleIcon} mr={4} /> Logout
              </MenuItem>
            </MenuGroup>
          </MenuList>
        </Menu>
      </Box>
    )
  }

  const drawerMotionBoxStyles = {
    lineHeight: '1rem',
    _active: { transform: 'translateY(1px)' },
    variants: itemVariants,
  }

  const DrawerNavigationItems = () => {
    return (
      <>
        <VStack
          spacing={10}
          alignItems='start'
          as={motion.div}
          variants={containerVariants}
          initial='hidden'
          animate='show'
        >
          {navItems.map((item, index) => (
            <MotionBox {...drawerMotionBoxStyles} key={index}>
              <Link fontWeight='bold' fontSize='2.2rem' href={item.url}>
                {item.name}
              </Link>
            </MotionBox>
          ))}
        </VStack>
        <VStack
          mt={10}
          alignItems='start'
          as={motion.div}
          variants={containerVariants}
          initial='hidden'
          animate='show'
        >
          <VStack align='left' gap={4}>
            {dropdownItems.map(
              (item, index) =>
                (item.visibility === undefined || item.visibility === true) && (
                  <MotionBox {...drawerMotionBoxStyles} key={index}>
                    <HStack gap={0.5}>
                      <Icon as={item.icon} mr={4} />
                      <Link
                        onClick={() => {
                          item.onClick()
                          closeDrawer()
                        }}
                      >
                        {item.name}
                      </Link>
                    </HStack>
                  </MotionBox>
                )
            )}
          </VStack>
          <Spacer />
          <MotionBox aria-label='Toggle color mode' {...drawerMotionBoxStyles}>
            <Link onClick={toggleColorMode}>
              <Icon as={colorMode === 'light' ? FaMoon : FaSun} mr={4} />
              {colorMode === 'light' ? 'Dark Mode' : 'Light Mode'}
            </Link>
          </MotionBox>

          <Spacer />
          <MotionBox {...drawerMotionBoxStyles}>
            <Link href='/' onClick={() => onLogout()}>
              <Icon as={ArrowLeftEndOnRectangleIcon} mr={4} />
              Logout
            </Link>
          </MotionBox>
        </VStack>
        <Text position='absolute' bottom='4'>
          Developed by Web Dev. Unit at the New York City Council
        </Text>
      </>
    )
  }

  return (
    <Box
      bg={bgColor}
      py={2}
      align='center'
      position='sticky'
      top='0'
      zIndex='sticky'
      className='print-hide'
    >
      <Flex mx='auto' px={2} align='center' justify='space-between'>
        <Box ml={{ base: 4, sm: 8 }}>
          <Link href='/' bg='blue'>
            {/* CC Logo Start */}
            <Box as='svg' width={100} height='auto' viewBox='0 0 315 235'>
              <path
                fill={logoColor}
                d='M174.849 112.759C172.819 110.208 171.934 106.511 171.934 101.825C171.934 92.3494 176.776 88.028 182.399 88.028C187.553 88.028 190.052 90.7354 191.458 93.3907C192.707 95.7857 193.488 99.7947 193.488 102.45C193.488 110.624 189.323 116.508 182.607 116.508C177.869 116.508 175.891 114.06 174.849 112.759ZM184.585 92.2974C183.961 92.037 183.284 91.9329 182.607 91.9329C177.452 91.9329 176.515 98.441 176.515 102.658C176.515 104.741 177.244 112.446 182.659 112.446C187.657 112.446 188.542 106.095 188.542 102.19C188.542 100.367 188.23 97.5038 187.605 95.7336C186.98 93.9634 185.835 92.818 184.585 92.2974ZM218.063 88.7049V115.05L213.742 116.612V111.561C213.742 111.353 213.638 110.885 213.429 110.885C213.221 110.885 213.117 110.885 212.961 111.041C212.651 111.377 212.302 111.853 211.934 112.357C211.561 112.868 211.167 113.406 210.774 113.852C209.16 115.57 207.129 116.56 204.943 116.56C201.975 116.56 199.476 114.685 199.476 109.687V88.7049H204.162V108.333C204.162 109.687 204.162 113.123 206.973 113.123C209.22 113.123 210.816 110.87 213.381 107.247C213.431 107.176 213.482 107.104 213.533 107.032V88.7049H218.063ZM230.142 115.935H225.456V89.7462L229.726 88.028V93.3387C229.726 93.5469 229.882 93.8593 230.142 93.8593C230.402 93.8593 230.767 93.4428 231.027 93.1304L232.069 91.7246C233.683 89.5379 235.869 88.2363 238.629 88.2363C239.878 88.2363 241.128 88.5487 242.169 89.2776C243.262 90.0065 244.148 91.204 244.148 95.1609V115.935H239.566V95.2651C239.566 94.5362 239.566 91.6726 236.858 91.6726C234.536 91.6726 232.636 94.0166 232.226 94.523C232.204 94.5503 232.186 94.5723 232.173 94.5882C230.559 96.7229 230.142 97.4518 230.142 97.5038V115.935ZM266.692 91.3081C267.681 92.6098 267.889 92.9742 268.462 94.2758L264.505 96.2543L264.488 96.2045C264.06 94.9726 263.004 91.9329 259.871 91.9329C256.643 91.9329 254.196 95.3171 254.196 100.94C254.196 103.231 254.352 106.199 255.029 108.073C255.758 110.051 257.32 112.499 260.288 112.499C263.151 112.499 264.505 110.051 264.921 107.292L269.087 109.166C268.202 113.644 264.921 116.508 260.288 116.508C249.875 116.508 249.614 104.272 249.614 102.762C249.614 100.159 249.614 95.8378 252.374 92.037C254.769 88.757 257.841 88.1322 259.715 88.1322C260.808 88.1322 264.557 88.4446 266.692 91.3081ZM281.183 83.6546H275.144V78.7084H281.183V83.6546ZM280.558 115.935H275.873V88.7049H280.558V115.935ZM294.997 115.935H290.311V78.8126L294.32 77.1465H294.997V115.935ZM174.849 156.423C172.819 153.872 171.934 150.176 171.934 145.49C171.934 136.014 176.776 131.693 182.399 131.693C187.553 131.693 190.052 134.4 191.458 137.055C192.707 139.45 193.488 143.459 193.488 146.115C193.488 154.289 189.323 160.172 182.607 160.172C177.869 160.172 175.891 157.725 174.849 156.423ZM184.585 135.962C183.961 135.702 183.284 135.598 182.607 135.598C177.452 135.598 176.515 142.106 176.515 146.323C176.515 148.405 177.244 156.111 182.659 156.111C187.657 156.111 188.542 149.759 188.542 145.854C188.542 144.032 188.23 141.168 187.605 139.398C186.98 137.628 185.835 136.483 184.585 135.962ZM204.11 159.599H199.424V133.411L203.693 131.693V137.003C203.693 137.212 203.849 137.524 204.11 137.524C204.37 137.524 204.734 137.107 204.995 136.795L206.036 135.389C207.65 133.203 209.837 131.901 212.596 131.901C213.846 131.901 215.095 132.213 216.137 132.942C217.23 133.671 218.115 134.869 218.115 138.826V159.599H213.533V138.93C213.533 138.201 213.533 135.337 210.826 135.337C208.504 135.337 206.604 137.681 206.193 138.188C206.171 138.215 206.154 138.237 206.14 138.253C204.526 140.387 204.11 141.116 204.11 141.168V159.599ZM230.142 159.599H225.456V133.411L229.726 131.693V137.003C229.726 137.212 229.882 137.524 230.142 137.524C230.402 137.524 230.767 137.107 231.027 136.795L232.069 135.389C233.683 133.203 235.869 131.901 238.629 131.901C239.878 131.901 241.128 132.213 242.169 132.942C243.262 133.671 244.148 134.869 244.148 138.826V159.599H239.566V138.93C239.566 138.201 239.566 135.337 236.858 135.337C234.536 135.337 232.636 137.681 232.226 138.188C232.204 138.215 232.186 138.237 232.173 138.253C230.559 140.387 230.142 141.116 230.142 141.168V159.599ZM261.669 131.745C268.229 131.745 271.718 137.316 271.874 143.98L271.926 146.479H255.786C255.734 152.571 257.504 156.632 262.346 156.632C265.429 156.632 266.942 155.208 267.79 154.411C267.816 154.387 267.841 154.364 267.865 154.341L270.572 156.84C268.802 158.402 266.667 160.276 261.721 160.276C255.421 160.276 250.996 155.278 250.996 146.739C250.996 132.89 259.222 131.745 261.669 131.745ZM255.951 143.135C255.948 143.194 255.945 143.25 255.942 143.303H267.24C267.24 138.617 265.47 135.129 261.773 135.129C259.534 135.129 257.764 136.483 256.879 138.565C256.093 140.431 256.002 142.158 255.951 143.135ZM293.741 134.973C294.73 136.274 294.938 136.639 295.511 137.94L291.554 139.919L291.537 139.869C291.109 138.637 290.053 135.598 286.92 135.598C283.692 135.598 281.245 138.982 281.245 144.605C281.245 146.896 281.402 149.863 282.078 151.738C282.807 153.716 284.369 156.163 287.337 156.163C290.201 156.163 291.554 153.716 291.971 150.957L296.136 152.831C295.251 157.309 291.971 160.172 287.337 160.172C276.924 160.172 276.664 147.937 276.664 146.427C276.664 143.824 276.664 139.502 279.423 135.702C281.818 132.422 284.89 131.797 286.764 131.797C287.858 131.797 291.606 132.109 293.741 134.973ZM302.453 137.524H298.6V134.556H302.505V128.36L306.879 124.143H307.399V134.556H311.721V137.524H307.139V153.716C307.139 154.237 307.139 156.163 309.43 156.163C310.992 156.163 312.71 155.33 314.012 154.653V158.766C312.606 159.547 310.263 160.12 308.701 160.12C307.452 160.12 302.453 159.704 302.453 154.289V137.524Z'
              />
              <path
                stroke={logoColor}
                fill='none'
                d='M196.583 59.1562L102.389 4L4 61.4544V173.682L102.389 230.753L196.583 176.363'
                strokeWidth='6.50811'
                strokeLinecap='round'
              />
              <path
                stroke={logoColor}
                fill='none'
                d='M171.796 73.9185L102.036 33.1104L29.1699 75.6188V158.652L102.036 200.877L171.796 160.636'
                strokeWidth='3.25405'
                strokeLinecap='round'
              />
            </Box>
            {/* CC Logo End */}
          </Link>
        </Box>
        <Flex
          ms={6}
          px={4}
          align='center'
          justify='space-between'
          display={{ base: 'none', md: 'flex' }} // Hide navigation container on smaller screens
          fontWeight='semibold'
          fontSize='lg'
          textTransform='uppercase'
          bg='nyccBlue.800'
          color='white'
          py={2}
          borderRadius='md'
          w='90%'
        >
          <NavigationItems />
          <HStack>
            <Avatar
              name={user?.full_name}
              onClick={() => navigate(`/directory/staff/${user?.id}`)}
              cursor='pointer'
            />

            <DropdownItems />
          </HStack>
        </Flex>
        {useBreakpointValue({
          base: (
            <IconButton
              aria-label='Toggle menu'
              variant='ghost'
              icon={<FaBars />}
              fontSize='2xl'
              width='3em'
              onClick={toggleDrawer}
            />
          ),
          md: <></>,
        })}
      </Flex>
      <Drawer
        placement='top'
        size='full'
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
      >
        <DrawerOverlay />
        <DrawerContent
          bg={colorMode === 'light' ? 'blue.100' : 'nyccBlue.800'}
          pt={70}
        >
          <DrawerCloseButton fontSize='2xl' mt={5} mr={5} />
          <DrawerBody>
            <DrawerNavigationItems />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  )
}

export default Navbar
